// deps
import React, { memo, useCallback, useMemo, useState } from "react";
import cx from "classnames";

// components
import { SwiggyMoneyInfoHalfCard } from "./components/swiggy-money-info-half-card";

// test-ids
import { SWIGGY_MONEY_INFO_TEST_IDS } from "@swiggy-private/payment-test-ids";

//interface
import { SwiggyMoneyInfoActions, SwiggyMoneyInfoHalfCardModel } from "@payment-ui/interfaces";

//hooks
import { useEffectOnce } from "@swiggy-private/core-ui";

//styles
import styles from "./styles.scss";

interface Props {
    info?: string;
    swiggyMoneyInfoHalfCardDetails?: SwiggyMoneyInfoHalfCardModel;
    canShowSwiggyMoneyInfoCta: boolean;
    infoClassName?: string;
    swiggyMoneyInfoCtaClassName?: string;
    actions?: SwiggyMoneyInfoActions;
}

function SwiggyMoneyInfo(props: Props): React.ReactElement {
    const {
        info,
        swiggyMoneyInfoHalfCardDetails,
        infoClassName,
        swiggyMoneyInfoCtaClassName,
        actions,
        canShowSwiggyMoneyInfoCta,
    } = props;

    const [showSwiggyMoneyInfoHalfCard, setShowSwiggyMoneyInfoHalfCard] = useState<boolean>(false);

    const swiggyMoneyInfoCtaText = useMemo(
        () =>
            canShowSwiggyMoneyInfoCta && swiggyMoneyInfoHalfCardDetails?.key ? swiggyMoneyInfoHalfCardDetails.key : "",
        [canShowSwiggyMoneyInfoCta, swiggyMoneyInfoHalfCardDetails],
    );

    useEffectOnce(() => {
        if (swiggyMoneyInfoCtaText && typeof actions?.onSwiggyMoneyInfoCtaImpression === "function") {
            actions.onSwiggyMoneyInfoCtaImpression();
        }
    });

    const toggleSwiggyMoneyInfoHalfCard = useCallback(
        () =>
            canShowSwiggyMoneyInfoCta &&
            setShowSwiggyMoneyInfoHalfCard((showSwiggyMoneyInfoHalfCard: boolean) => !showSwiggyMoneyInfoHalfCard),
        [canShowSwiggyMoneyInfoCta],
    );

    const onSwiggyMoneyInfoCtaClick = useCallback(
        e => {
            e.stopPropagation();
            if (typeof actions?.onSwiggyMoneyInfoCtaClick === "function") {
                actions.onSwiggyMoneyInfoCtaClick();
            }
            toggleSwiggyMoneyInfoHalfCard();
        },
        [toggleSwiggyMoneyInfoHalfCard, actions],
    );

    const swiggyMoneyInfoCtaClass = useMemo(() => cx(styles.swiggyMoneyInfoCta, swiggyMoneyInfoCtaClassName), [
        swiggyMoneyInfoCtaClassName,
    ]);

    return (
        <>
            {info ? (
                <span data-testid={SWIGGY_MONEY_INFO_TEST_IDS.MESSAGE} className={infoClassName}>
                    {info}
                </span>
            ) : null}
            {swiggyMoneyInfoCtaText ? (
                <span
                    onClick={onSwiggyMoneyInfoCtaClick}
                    className={swiggyMoneyInfoCtaClass}
                    data-testid={SWIGGY_MONEY_INFO_TEST_IDS.CTA}
                >
                    {swiggyMoneyInfoCtaText}
                </span>
            ) : null}
            {showSwiggyMoneyInfoHalfCard && swiggyMoneyInfoHalfCardDetails ? (
                <SwiggyMoneyInfoHalfCard
                    onClose={toggleSwiggyMoneyInfoHalfCard}
                    heading={swiggyMoneyInfoHalfCardDetails.heading}
                    message={swiggyMoneyInfoHalfCardDetails.message}
                    ctaMessage={swiggyMoneyInfoHalfCardDetails.ctaMessage}
                    onClickSwiggyMoneyInfoHalfCardActionButton={actions?.onClickSwiggyMoneyInfoHalfCardActionButton}
                    onClickSwiggyMoneyInfoHalfCardCloseButton={actions?.onClickSwiggyMoneyInfoHalfCardCloseButton}
                    onImpressionSwiggyMoneyInfoHalfCard={actions?.onImpressionSwiggyMoneyInfoHalfCard}
                />
            ) : null}
        </>
    );
}

export default memo(SwiggyMoneyInfo);
